import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  TextField,
  Box,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import Modal from '../../../../components/Modal';
import Form from '../../../../components/useForm';
import { Controls } from '../../../../components/controls/Controls';

export default function TelusOperatingHoursModal(props) {
  const {
    isOpen,
    handleClose,
    setting,
    handleSave,
  } = props;

  const parseHours = (value) => {
    try {
      return value ? JSON.parse(value) : {
        start: '07:00',
        end: '19:00',
      };
    } catch {
      return {
        start: '07:00',
        end: '19:00',
      };
    }
  };

  const [hours, setHours] = useState(parseHours(setting.value));
  const [error, setError] = useState('');

  const handleTimeChange = (field, value) => {
    setHours((prev) => ({
      ...prev,
      [field]: value,
    }));
    setError('');
  };

  const validateTimeFormat = (timeStr) => {
    // Parse using Luxon with HH:mm format (24-hour)
    const time = DateTime.fromFormat(timeStr, 'HH:mm');
    return time.isValid;
  };

  const validateTimeRange = (range) => {
    const { start, end } = range;

    // Validate both formats
    if (!validateTimeFormat(start) || !validateTimeFormat(end)) {
      return false;
    }

    const startTime = DateTime.fromFormat(start, 'HH:mm');
    const endTime = DateTime.fromFormat(end, 'HH:mm');

    // Example logic: ensure end is after start
    return endTime > startTime;
  };

  const handleSaveChanges = () => {
    if (!validateTimeRange(hours)) {
      setError('Please enter valid times in HH:MM format (24-hour)');
      return;
    }

    const startTime = hours.start.split(':').map(Number);
    const endTime = hours.end.split(':').map(Number);
    const startMinutes = startTime[0] * 60 + startTime[1];
    const endMinutes = endTime[0] * 60 + endTime[1];

    if (startMinutes >= endMinutes) {
      setError('End time must be after start time');
      return;
    }

    handleSave(setting, JSON.stringify(hours));
    handleClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      handleClose={handleClose}
      title="Operating Hours Configuration"
      size="sm"
    >
      <Form>
        <Grid container spacing={3}>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Controls.Button
              text="Save"
              onClick={handleSaveChanges}
              sx={{ minWidth: '100px', height: '40px' }}
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Start Time
                </Typography>
                <TextField
                  fullWidth
                  type="time"
                  value={hours.start}
                  onChange={(e) => handleTimeChange('start', e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    step: 300, // 5 min intervals
                  }}
                  variant="outlined"
                />
              </Box>
              <Typography variant="body1" sx={{ mt: 3 }}>
                to
              </Typography>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  End Time
                </Typography>
                <TextField
                  fullWidth
                  type="time"
                  value={hours.end}
                  onChange={(e) => handleTimeChange('end', e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    step: 300, // 5 min intervals
                  }}
                  variant="outlined"
                />
              </Box>
            </Box>
            {error && (
              <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                {error}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="textSecondary">
              Enter operating hours in 24-hour format (e.g., 07:00 for 7:00 AM, 19:00 for 7:00 PM)
            </Typography>
          </Grid>
        </Grid>
      </Form>
    </Modal>
  );
}

TelusOperatingHoursModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  setting: PropTypes.shape({
    name: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
  handleSave: PropTypes.func.isRequired,
};
